import React, { MouseEvent, useEffect, useMemo } from "react";
import styles from "./sectionContent.module.scss";
import { isHomeEDApp } from "@constants";
import { Content, ContentType, Lesson } from "@src/types";
import IconPDF from "@src/assets/icons/content_type_pdf.svg?react";
import IconAudio from "@src/assets/icons/content_type_audio.svg?react";
import IconSlide from "@src/assets/icons/content_type_slide.svg?react";
import IconVideo from "@src/assets/icons/content_type_video.svg?react";
import IconForm from "@src/assets/icons/content_type_form.svg?react";
import { useCourseContent } from "@src/hooks/useCourseContent";
import { useAnalytics, Events, useUser } from "@hooks";
import {
  useUpdateLastVisitedLessonMutation,
  LastVisitedLessonContentFragmentDoc,
  LastVisitedLessonContentFragment as LastVisitedLesson,
  SectionFragment,
} from "@api/graphql-codegen";
import Checkbox from "@src/components/checkbox/Checkbox";
import { logger } from '../../../../../utils/logger';

type Props = {
  content: Content;
  lesson?: Lesson | SectionFragment;
  lastVisitedLesson?: LastVisitedLesson;
  selected?: boolean;
  disabled?: boolean;
  closeNav?: () => void;
};

const getContentIcon = (type: ContentType) => {
  switch (type) {
    case ContentType.PDF: {
      return IconPDF;
    }
    case ContentType.AUDIO: {
      return IconAudio;
    }
    // case ContentType.GOOGLESLIDE:
    case ContentType.SLIDE: {
      return IconSlide;
    }
    case ContentType.REVEALSLIDE:
    case ContentType.ZIPSLIDE: {
      return IconSlide;
    }
    case ContentType.VIDEO: {
      return IconVideo;
    }
    case ContentType.FORM: {
      return IconForm;
    }
    default: {
      return null;
    }
  }
};

function SectionContent(props: Props) {
  const { content, selected, lesson, lastVisitedLesson, disabled, closeNav } =
    props;
  const { name } = content;
  const {
    program,
    setContent,
    setLesson,
    setSection,
    currentUnit,
    setProgram,
    setSelectionLevel,
    isParentApp,
    isPreview,
    section,
    isNewLessonLayout,
    content: selectedContent,
  } = useCourseContent();

  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  const [updateLastVisitedLesson] = useUpdateLastVisitedLessonMutation({
    onError(e) {
      logger.debug(`[e]`, e);
    },
    update(cache, result) {
      if (result.data?.updateLastVisitedLesson) {
        const lastVisited = result.data.updateLastVisitedLesson;
        cache.writeFragment({
          id: cache.identify(lastVisited),
          fragment: LastVisitedLessonContentFragmentDoc,
          data: {
            ...lastVisited,
          },
        });
      }
    },
  });

  const Icon = getContentIcon(content.type);

  useEffect(() => {
    if (
      lastVisitedLesson &&
      lastVisitedLesson.moduleId === content?.id &&
      program
    ) {
      setProgram(program);
      if (lesson?.__typename === "Lesson") {
        setLesson(lesson);
        setSection(undefined);
      } else if (lesson?.__typename === "Section") {
        setSection(lesson);
      }
      setContent(content);
      setSelectionLevel("Content");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, program, lastVisitedLesson]);

  const handleSelect = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setContent(content);
    if (lesson?.__typename === "Lesson") {
      setLesson(lesson);
      setSection(undefined);
    } else if (lesson?.__typename === "Section") {
      setSection(lesson);
    }
    setSelectionLevel("Content");
    closeNav && closeNav();

    const curIndex =
      lesson?.modules?.findIndex((m) => m.id === content.id) || 0;
    const prevModule =
      curIndex >= 1 ? lesson?.modules?.at(curIndex - 1) : undefined;

    if (
      !isHomeEDApp &&
      !isParentApp &&
      !isPreview &&
      content &&
      currentUnit?.id &&
      lesson?.id
    ) {
      updateLastVisitedLesson({
        variables: {
          input: {
            lessonId: lesson?.id,
            moduleId: content.id,
            previousModuleId: prevModule?.id,
            unitId: currentUnit?.id || "",
            sectionId: section?.id,
          },
        },
      });

      const _d = {
        ...Events.mappers.program(program),
        ...Events.mappers.lesson(lesson),
        ...Events.mappers.content(content),
        content_type: content.type,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      };

      if (isPreview && lesson?.id) {
        trackEvent(Events.actions.previewLessonModulesViewed, _d);
      } else if (lesson?.id && !isPreview) {
        trackEvent(Events.actions.contentOpened, _d);
      }
    }

    if (isHomeEDApp && !isParentApp && content && currentUnit?.id) {
      const _d = {
        ...Events.mappers.lesson(lesson),
        ...Events.mappers.content(content),
        content_type: content.type,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      };

      if (isPreview && lesson?.id) {
        trackEvent(Events.actions.previewLessonModulesViewed, _d);
      } else if (lesson?.id && !isPreview) {
        trackEvent(Events.actions.contentOpened, _d);
      }
    }
  };

  const handleCheck = (e: any) => {
    if (lesson?.__typename === "Lesson") {
      setLesson(lesson);
      setSection(undefined);
    } else if (lesson?.__typename === "Section") {
      setSection(lesson);
    }
    setSelectionLevel("Content");
    closeNav && closeNav();

    if (content === selectedContent) {
      const curIndex =
        lesson?.modules?.findIndex((m) => m.id === content.id) || 0;
      const prevModule =
        curIndex >= 1 ? lesson?.modules?.at(curIndex - 1) : undefined;
      if (prevModule) {
        setContent(prevModule);
      }
    } else {
      setContent(content);
    }
  };

  const wrapperClassName = useMemo(() => {
    const classes = [isNewLessonLayout ? styles.checkwrapper : styles.wrapper];
    if (selected && selectedContent === content) {
      classes.push(styles.selected);
    }
    if (disabled) {
      classes.push(styles.disabled);
    }
    return classes.join(" ");
  }, [disabled, selected, isNewLessonLayout, selectedContent, content]);

  if (isNewLessonLayout) {
    return (
      <div className={wrapperClassName}>
        <Checkbox
          id={`check_${name}`}
          label={name}
          checked={selected ?? false}
          className={styles.itemView}
          onChange={handleCheck}
        />
        <div className={styles.meta}></div>
        <div className={styles.typeIconWrapper}>
          {content.type === ContentType.PARENT ? (
            <div className={styles.paColor}>PS</div>
          ) : (
            Icon && <Icon className={styles.typeIcon} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={wrapperClassName}
      onClick={disabled ? () => {} : handleSelect}
    >
      <div className={styles.circle} />
      <div className={styles.title}>{name}</div>
      <div className={styles.meta}></div>
      <div className={styles.typeIconWrapper}>
        {content.type === ContentType.PARENT ? (
          <div className={styles.paColor}>PS</div>
        ) : (
          Icon && <Icon className={styles.typeIcon} />
        )}
      </div>
    </div>
  );
}

export default React.memo(SectionContent);
